import { CustomTooltip } from '@cian/ui-kit';

import { type CSSProperties, type FC, useCallback, useEffect, useRef, useState } from 'react';

import { SERIA_PLUS_IMAGE_HEIGHT, SERIA_PLUS_IMAGE_WIDTH } from '../../constants';
import { type SeriaPlusSlideMarker } from '../../settings/seriaPlusTypes';

import styles from './SeriaPlusMarker.css';

type MarkerStyle = CSSProperties & {
  '--marker-top-left-x': string;
  '--marker-top-left-y': string;
};

type SeriaPlusMarkerProps = {
  marker: SeriaPlusSlideMarker;
  onTooltipClose?: () => void;
  onTooltipOpen?: () => void;
};

function getMarkerPositionStyle(marker: SeriaPlusSlideMarker): MarkerStyle {
  // marker.left/top — координаты левого верхнего угла кнопки в макете Figma, а не центр точки.
  return {
    '--marker-top-left-x': `${(marker.left / SERIA_PLUS_IMAGE_WIDTH) * 100}%`,
    '--marker-top-left-y': `${(marker.top / SERIA_PLUS_IMAGE_HEIGHT) * 100}%`,
  };
}

export const SeriaPlusMarker: FC<SeriaPlusMarkerProps> = ({ marker, onTooltipClose, onTooltipOpen }) => {
  const markerRef = useRef<HTMLButtonElement | null>(null);
  const wasTooltipOpenRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isTooltipOpen = isFocused || isHovered;

  useEffect(() => {
    if (wasTooltipOpenRef.current === isTooltipOpen) {
      return;
    }

    wasTooltipOpenRef.current = isTooltipOpen;

    if (isTooltipOpen) {
      onTooltipOpen?.();
    } else {
      onTooltipClose?.();
    }
  }, [isTooltipOpen, onTooltipClose, onTooltipOpen]);

  useEffect(() => {
    return (): void => {
      if (wasTooltipOpenRef.current) {
        onTooltipClose?.();
      }
    };
  }, [onTooltipClose]);

  const handleTooltipMouseEnter = useCallback((): void => {
    setIsHovered(true);
  }, []);

  const handleTooltipMouseLeave = useCallback((): void => {
    setIsHovered(false);
  }, []);

  const handleTooltipFocus = useCallback((): void => {
    setIsFocused(true);
  }, []);

  const handleTooltipBlur = useCallback((): void => {
    setIsFocused(false);
  }, []);

  const tooltipId = isTooltipOpen && marker.tooltip ? `seria-plus-carousel-marker-tooltip-${marker.id}` : undefined;

  return (
    <>
      <button
        aria-describedby={tooltipId}
        aria-expanded={isTooltipOpen}
        aria-label={marker.tooltip?.text || 'Особенность на изображении'}
        className={styles['marker']}
        data-testid="seria-plus-carousel-marker"
        style={getMarkerPositionStyle(marker)}
        type="button"
        ref={markerRef}
        onBlur={handleTooltipBlur}
        onFocus={handleTooltipFocus}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
        onPointerEnter={handleTooltipMouseEnter}
        onPointerLeave={handleTooltipMouseLeave}
      />
      {marker.tooltip && (
        <CustomTooltip
          anchorRef={markerRef}
          content={
            <span
              className={styles['tooltip-content']}
              data-testid="seria-plus-carousel-marker-tooltip"
              id={tooltipId}
              role="tooltip"
            >
              {marker.tooltip.text}
            </span>
          }
          open={isTooltipOpen}
          placement={marker.tooltip.placement}
          size="M"
          theme="white"
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        />
      )}
    </>
  );
};
